import { useEffect, useState } from "react";
import {
  Folder,
  FileText,
  Download,
  Trash2,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { toast } from "react-toastify";

const ViewResourcesModal = ({ isOpen, batchNo, onClose }) => {
  const [resources, setResources] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [deletingKey, setDeletingKey] = useState(null);

  useEffect(() => {
    if (isOpen && batchNo) loadResources();
  }, [isOpen, batchNo]);

  const loadResources = async () => {
    try {
      const res = await fetch(
        `https://trainer-portal-78f5.onrender.com/api/lms/resources?batchNo=${batchNo}`,
      );
      const data = await res.json();
      const items = data.data || data.files || data.resources || [];
      setResources(items);

      // Auto-expand all folders on load
      const allFolders = {};
      items.forEach((item) => {
        const folder = extractFolderName(item.s3Key || item.key || "");
        if (folder) allFolders[folder] = true;
      });
      setExpandedFolders(allFolders);
    } catch (err) {
      console.error("Error loading resources:", err);
      toast.error("Failed to load resources");
      setResources([]);
    }
  };

  const handleDownload = async (key) => {
    try {
      toast.info("Preparing download...");
      const res = await fetch(
        `https://trainer-portal-78f5.onrender.com/api/lms/download?key=${encodeURIComponent(key)}`,
      );
      const data = await res.json();
      window.open(data.url, "_blank");
      toast.success("Opening file...");
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Failed to download file");
    }
  };

  const handleDelete = async (resourceId, key) => {
    if (!key) {
      toast.error("Invalid file key");
      return;
    }

    // Custom confirmation using browser's confirm (can be replaced with a custom modal)
    const confirmed = window.confirm(
      "Are you sure you want to delete this file?",
    );
    if (!confirmed) return;

    setDeletingKey(key);

    try {
      const res = await fetch(
        `https://trainer-portal-78f5.onrender.com/api/lms/delete-resource?s3Key=${encodeURIComponent(key)}`,
        { method: "DELETE" },
      );

      const data = await res.json();

      if (data.success) {
        toast.success("File deleted successfully");
        loadResources();
      } else {
        toast.error(data.message || "Delete failed");
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Failed to delete file");
    } finally {
      setDeletingKey(null);
    }
  };

  const extractFolderName = (path) => {
    if (!path) return "Uncategorized";
    const withoutPrefix = path.replace(/^Resources\/\d+\//, "");
    const parts = withoutPrefix.split("/");
    return parts.length > 1 ? parts[0] : "Uncategorized";
  };

  const extractFileName = (path) => {
    if (!path) return "Unknown File";
    const fileName = path.split("/").pop();
    const cleaned = fileName.replace(
      /^\d{4}-\d{2}-\d{2}T[\d:.]+Z_[a-f0-9-]+_/i,
      "",
    );
    return cleaned || fileName;
  };

  const toggleFolder = (folderName) => {
    setExpandedFolders((prev) => ({
      ...prev,
      [folderName]: !prev[folderName],
    }));
  };

  if (!isOpen) return null;

  // Group resources by folder
  const grouped = {};
  resources.forEach((item) => {
    const folder = extractFolderName(item.s3Key || item.key || "");
    if (!grouped[folder]) grouped[folder] = [];
    grouped[folder].push(item);
  });

  const folderNames = Object.keys(grouped).sort();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg w-full max-w-[700px] max-h-[95vh] sm:max-h-[85vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
            Resources for Batch #{batchNo}
          </h2>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">
            {resources.length} file{resources.length !== 1 ? "s" : ""} in{" "}
            {folderNames.length} folder{folderNames.length !== 1 ? "s" : ""}
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-3 sm:py-4">
          {resources.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <Folder size={48} strokeWidth={1.5} className="sm:w-16 sm:h-16" />
              <p className="mt-4 text-sm sm:text-base text-gray-500">
                No resources uploaded yet
              </p>
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-3">
              {folderNames.map((folderName) => (
                <div
                  key={folderName}
                  className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50"
                >
                  {/* Folder Header */}
                  <button
                    onClick={() => toggleFolder(folderName)}
                    className="w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 bg-gradient-to-r from-blue-50 to-gray-50 hover:from-blue-100 hover:to-gray-100 transition-colors"
                  >
                    {expandedFolders[folderName] ? (
                      <ChevronDown
                        size={16}
                        className="sm:w-[18px] sm:h-[18px] text-gray-600 flex-shrink-0"
                      />
                    ) : (
                      <ChevronRight
                        size={16}
                        className="sm:w-[18px] sm:h-[18px] text-gray-600 flex-shrink-0"
                      />
                    )}
                    <Folder
                      size={18}
                      className="sm:w-5 sm:h-5 text-blue-600 flex-shrink-0"
                    />
                    <span className="font-medium text-sm sm:text-base text-gray-800 flex-1 text-left truncate">
                      {folderName}
                    </span>
                    <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full flex-shrink-0">
                      {grouped[folderName].length}
                    </span>
                  </button>

                  {/* Files List */}
                  {expandedFolders[folderName] && (
                    <div className="bg-white divide-y divide-gray-100">
                      {grouped[folderName].map((resource) => {
                        const fileName = extractFileName(
                          resource.s3Key || resource.key,
                        );
                        const fileExt = fileName
                          .split(".")
                          .pop()
                          ?.toLowerCase();
                        const isDeleting =
                          deletingKey === (resource.s3Key || resource.key);

                        return (
                          <div
                            key={resource.resourceId || resource.s3Key}
                            className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3 hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-start sm:items-center gap-2 sm:gap-3 flex-1 min-w-0">
                              <FileText
                                size={16}
                                className="sm:w-[18px] sm:h-[18px] text-gray-400 flex-shrink-0 mt-0.5 sm:mt-0"
                              />

                              <div className="flex-1 min-w-0">
                                <p className="text-xs sm:text-sm text-gray-700 truncate font-medium">
                                  {fileName}
                                </p>
                                <p className="text-xs text-gray-400 mt-0.5">
                                  {fileExt?.toUpperCase()} file
                                </p>
                              </div>
                            </div>

                            <div className="flex gap-2 ml-6 sm:ml-0">
                              <button
                                onClick={() =>
                                  handleDownload(resource.s3Key || resource.key)
                                }
                                disabled={isDeleting}
                                className="flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="View/Download"
                              >
                                <Download
                                  size={12}
                                  className="sm:w-[14px] sm:h-[14px]"
                                />
                                <span className="hidden sm:inline">View</span>
                                <span className="sm:hidden">Get</span>
                              </button>
                              <button
                                onClick={() =>
                                  handleDelete(
                                    resource.resourceId,
                                    resource.s3Key || resource.key,
                                  )
                                }
                                disabled={isDeleting}
                                className="flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1.5 bg-red-600 text-white rounded-md text-xs font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Delete"
                              >
                                <Trash2
                                  size={12}
                                  className="sm:w-[14px] sm:h-[14px]"
                                />
                                {isDeleting ? (
                                  "..."
                                ) : (
                                  <span className="hidden sm:inline">
                                    Delete
                                  </span>
                                )}
                                {isDeleting ? (
                                  ""
                                ) : (
                                  <span className="sm:hidden">Del</span>
                                )}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md text-sm font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewResourcesModal;
